/**
 * Diwali Special Festive Frame and Watermark Generator
 * Provides self-contained SVG motifs and CSS styling for Diwali festival invoices.
 * Supports 4 distinct festive layouts:
 * 1. diwali_frame: Royal Gold Frame with 4 Upright Corner Diyas & Company Diwali Greeting Center Watermark
 * 2. diwali_wishes / diwali_diya: Company Diwali Greeting Center Watermark with Luxury Gold Highlights (Zero corner clipping)
 * 3. diwali_toran: Festive Marigold & Mango Leaf Toran with Hanging Brass Lamps & Company Greeting
 * 4. diwali_rangoli: Auspicious Shubh-Labh Rangoli with 4-Corner Geometric Medallions & Company Greeting
 */

export const getDiwaliWatermarkCSS = (watermarkType) => {
  if (!watermarkType || watermarkType === 'none') return '';

  return `
    /* Standard Watermarks */
    .watermark-container {
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      z-index: 0;
      pointer-events: none;
      user-select: none;
      overflow: hidden;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .watermark {
      transform: rotate(-45deg);
      font-size: 90px;
      font-weight: bold;
      color: rgba(180, 180, 180, 0.14);
      white-space: nowrap;
      letter-spacing: 2px;
    }
    .watermark.stylized {
      transform: rotate(-35deg);
      font-size: 110px;
      font-weight: normal;
      font-family: 'Brush Script MT', 'Lucida Handwriting', 'Snell Roundhand', 'Monotype Corsiva', 'Dancing Script', cursive;
      color: rgba(180, 180, 180, 0.12);
      letter-spacing: 2px;
      text-shadow: 2px 2px 10px rgba(0,0,0,0.05);
    }

    /* A4 Container Setup */
    .invoice-container {
      position: relative !important;
      min-height: 297mm !important;
      box-sizing: border-box !important;
    }
    
    /* Frame & Layout Inner Padding */
    .invoice-container.has-diwali-frame {
      padding: 16mm 14mm 14mm 14mm !important;
    }
    .invoice-container.has-diwali-toran {
      padding: 18mm 14mm 14mm 14mm !important;
    }

    /* =======================================================
       LAYOUT 1: DIWALI FESTIVE FRAME
       ======================================================= */
    .diwali-frame-container {
      position: absolute;
      top: 6mm;
      left: 6mm;
      right: 6mm;
      bottom: 6mm;
      pointer-events: none;
      z-index: 1;
      border: 1.5px solid #c59b27;
      border-radius: 4px;
      box-sizing: border-box;
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.5);
    }
    .diwali-frame-container::before {
      content: '';
      position: absolute;
      top: 2.5mm;
      left: 2.5mm;
      right: 2.5mm;
      bottom: 2.5mm;
      border: 0.75px solid rgba(197, 155, 39, 0.55);
      border-radius: 2px;
      pointer-events: none;
    }

    /* Corner Diyas - All upright with flames facing upward */
    .diwali-corner {
      position: absolute;
      width: 54px;
      height: 54px;
      pointer-events: none;
      z-index: 2;
    }
    .corner-tl { top: -2px; left: -2px; }
    .corner-tr { top: -2px; right: -2px; }
    .corner-bl { bottom: -2px; left: -2px; }
    .corner-br { bottom: -2px; right: -2px; }

    /* Royal Header Crest */
    .diwali-top-banner {
      position: absolute;
      top: -12px;
      left: 50%;
      transform: translateX(-50%);
      background: #ffffff;
      border: 1.5px solid #c59b27;
      box-shadow: 0 3px 8px rgba(180, 130, 20, 0.15);
      padding: 3px 18px;
      border-radius: 16px;
      display: flex;
      align-items: center;
      gap: 8px;
      white-space: nowrap;
      z-index: 3;
    }
    .diwali-banner-diya {
      flex-shrink: 0;
      display: inline-block;
      vertical-align: middle;
    }
    .diwali-crest-leaf {
      color: #c59b27;
      font-size: 11px;
      display: inline-block;
    }
    .diwali-badge-hindi {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Cinzel', Georgia, serif;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 1.5px;
      color: #92400e;
    }
    .diwali-badge-eng {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Cinzel', Georgia, serif;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 2.2px;
      color: #b45309;
    }
    .diwali-sparkle {
      color: #c59b27;
      font-size: 10px;
    }

    /* Royal Footer Flourish */
    .diwali-bottom-banner {
      position: absolute;
      bottom: -9px;
      left: 50%;
      transform: translateX(-50%);
      background: #ffffff;
      border: 1px solid #c59b27;
      padding: 1px 12px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      gap: 6px;
      white-space: nowrap;
      z-index: 3;
    }
    .diwali-flourish-diya {
      color: #c59b27;
      font-size: 11px;
    }

    /* =======================================================
       COMPANY DIWALI GREETING CENTER WATERMARK
       Glitch-free, pure SVG center watermark with zero corner clipping
       ======================================================= */
    .diwali-wishes-watermark-container {
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      z-index: 0;
      pointer-events: none;
      user-select: none;
      overflow: visible;
      display: flex;
      justify-content: center;
      align-items: center;
      opacity: 0.14;
    }
    .diwali-wishes-watermark-svg {
      width: 86%;
      max-width: 620px;
      height: auto;
      display: block;
      overflow: visible;
    }

    /* =======================================================
       LAYOUT 3: DIWALI ROYAL TORAN
       ======================================================= */
    .diwali-toran-container {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      pointer-events: none;
      z-index: 3;
      overflow: visible;
    }
    .diwali-toran-svg {
      width: 100%;
      height: 64px;
      display: block;
    }
    .diwali-toran-border {
      position: absolute;
      top: 6mm;
      left: 6mm;
      right: 6mm;
      bottom: 6mm;
      border: 1px solid rgba(197, 155, 39, 0.45);
      border-radius: 4px;
      pointer-events: none;
      z-index: 1;
    }
    .diwali-toran-border::after {
      content: '';
      position: absolute;
      top: 2mm;
      left: 2mm;
      right: 2mm;
      bottom: 2mm;
      border: 0.5px dashed rgba(197, 155, 39, 0.35);
      border-radius: 2px;
    }

    /* =======================================================
       LAYOUT 4: DIWALI SHUBH LABH RANGOLI
       ======================================================= */
    .diwali-rangoli-container {
      position: absolute;
      top: 6mm;
      left: 6mm;
      right: 6mm;
      bottom: 6mm;
      pointer-events: none;
      z-index: 1;
      border: 1.5px solid #c59b27;
      border-radius: 4px;
      box-sizing: border-box;
    }
    .diwali-rangoli-container::before {
      content: '';
      position: absolute;
      top: 2.5mm;
      left: 2.5mm;
      right: 2.5mm;
      bottom: 2.5mm;
      border: 0.75px dashed rgba(197, 155, 39, 0.5);
      border-radius: 2px;
      pointer-events: none;
    }
    .diwali-rangoli-corner {
      position: absolute;
      width: 58px;
      height: 58px;
      pointer-events: none;
      z-index: 2;
    }
    .rangoli-corner-tl { top: -2px; left: -2px; }
    .rangoli-corner-tr { top: -2px; right: -2px; transform: scaleX(-1); }
    .rangoli-corner-bl { bottom: -2px; left: -2px; transform: scaleY(-1); }
    .rangoli-corner-br { bottom: -2px; right: -2px; transform: scale(-1, -1); }

    .diwali-side-badge {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      background: #ffffff;
      border: 1px solid #c59b27;
      box-shadow: 0 2px 6px rgba(180, 130, 20, 0.15);
      width: 24px;
      height: 60px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 3;
    }
    .badge-shubh { left: -13px; }
    .badge-labh { right: -13px; }
    .side-badge-text {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, serif;
      font-size: 11px;
      font-weight: 800;
      color: #92400e;
      writing-mode: vertical-rl;
      text-orientation: upright;
      letter-spacing: 2px;
    }

    /* Print & A4 Page Consistency */
    @media print {
      @page {
        size: A4 portrait;
        margin: 0;
      }
      body {
        margin: 0 !important;
        padding: 0 !important;
        background: #ffffff !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      .invoice-container {
        width: 210mm !important;
        height: 297mm !important;
        min-height: 297mm !important;
        max-width: 210mm !important;
        margin: 0 !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        page-break-after: avoid;
        page-break-inside: avoid;
        box-sizing: border-box !important;
      }
      .diwali-frame-container, .diwali-toran-border, .diwali-rangoli-container {
        top: 6mm !important;
        left: 6mm !important;
        right: 6mm !important;
        bottom: 6mm !important;
      }
    }

    /* Keep foreground elements crisp and in front of watermarks */
    .header-section, .transport-details, .address-section, .items-table, .totals-section, .footer-section, .bank-details, .terms-conditions {
      position: relative;
      z-index: 2;
    }
  `;
};

/**
 * Returns an upright Diya positioned inside the requested corner bracket
 */
const getCornerDiyaSVG = (pos = 'tl') => {
  let bracketPath = '';
  let dashPath = '';
  let diyaX = 13;
  let diyaY = 13;
  let sparkleX = 42;
  let sparkleY = 11;

  if (pos === 'tl') {
    bracketPath = 'M2 52 L2 2 L52 2';
    dashPath = 'M6 44 L6 6 L44 6';
    diyaX = 13;
    diyaY = 13;
    sparkleX = 42;
    sparkleY = 11;
  } else if (pos === 'tr') {
    bracketPath = 'M2 2 L52 2 L52 52';
    dashPath = 'M10 6 L48 6 L48 44';
    diyaX = 15;
    diyaY = 13;
    sparkleX = 12;
    sparkleY = 11;
  } else if (pos === 'bl') {
    bracketPath = 'M2 2 L2 52 L52 52';
    dashPath = 'M6 10 L6 48 L44 48';
    diyaX = 13;
    diyaY = 17;
    sparkleX = 42;
    sparkleY = 43;
  } else if (pos === 'br') {
    bracketPath = 'M52 2 L52 52 L2 52';
    dashPath = 'M48 10 L48 48 L10 48';
    diyaX = 15;
    diyaY = 17;
    sparkleX = 12;
    sparkleY = 43;
  }

  return `
    <svg width="54" height="54" viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="${bracketPath}" stroke="#c59b27" stroke-width="1.8" stroke-linecap="round"/>
      <path d="${dashPath}" stroke="#e5c158" stroke-width="0.8" stroke-dasharray="2,2" opacity="0.85"/>
      <g transform="translate(${diyaX}, ${diyaY}) scale(0.38)">
        <path d="M4 38 C4 54, 52 54, 52 38 C52 36, 4 36, 4 38 Z" fill="#b45309" stroke="#78350f" stroke-width="1"/>
        <path d="M8 37 C8 49, 48 49, 48 37 Z" fill="#d97706"/>
        <ellipse cx="28" cy="36" rx="20" ry="4" fill="#78350f"/>
        <ellipse cx="28" cy="35" rx="17" ry="3" fill="#f59e0b"/>
        <circle cx="28" cy="18" r="14" fill="#fef08a" opacity="0.4"/>
        <circle cx="28" cy="18" r="9" fill="#fde047" opacity="0.55"/>
        <path d="M28 2 C21 12, 23 21, 28 26 C33 21, 35 12, 28 2 Z" fill="#ea580c"/>
        <path d="M28 8 C24 15, 25 20, 28 23 C31 20, 32 15, 28 8 Z" fill="#f59e0b"/>
        <path d="M28 13 C26 17, 26.5 20, 28 22 C29.5 20, 30 17, 28 13 Z" fill="#fef08a"/>
      </g>
      <polygon points="${sparkleX},${sparkleY-3} ${sparkleX+1},${sparkleY-1} ${sparkleX+3},${sparkleY} ${sparkleX+1},${sparkleY+1} ${sparkleX},${sparkleY+3} ${sparkleX-1},${sparkleY+1} ${sparkleX-3},${sparkleY} ${sparkleX-1},${sparkleY-1}" fill="#c59b27" opacity="0.9"/>
    </svg>
  `;
};

/**
 * Company Diwali Wishes Center Watermark (Zero Corner Clipping):
 * Uses a generous 900x600 viewBox with gentle -15deg rotation so all four corners
 * have 100px to 145px of margin inside the SVG, completely eliminating clipped corners!
 */
const getCompanyDiwaliWatermarkHTML = (companyName = '') => {
  const displayCompany = (companyName && companyName.trim() !== '') ? companyName.trim() : 'OUR COMPANY';
  const len = displayCompany.length;
  const companyFontSize = len > 32 ? 20 : len > 24 ? 24 : len > 16 ? 28 : 32;
  const sanitizedCompany = displayCompany
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return `
    <div class="diwali-wishes-watermark-container">
      <svg class="diwali-wishes-watermark-svg" viewBox="0 0 900 600" fill="none" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="diwaliGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#fef3c7" stop-opacity="0.65" />
            <stop offset="70%" stop-color="#fef08a" stop-opacity="0.18" />
            <stop offset="100%" stop-color="#ffffff" stop-opacity="0" />
          </radialGradient>
        </defs>

        <g transform="rotate(-15 450 300)">
          <!-- Outer Highlight Cartouche - Centered at (450, 300), safe >100px bounds preventing any corner cutting -->
          <rect x="170" y="170" width="560" height="260" rx="20" fill="url(#diwaliGlow)" stroke="#c59b27" stroke-width="1.8" stroke-dasharray="6,4" />
          <rect x="178" y="178" width="544" height="244" rx="16" fill="none" stroke="#e5c158" stroke-width="0.8" opacity="0.65" />

          <!-- Corner Decorative Accent Stars -->
          <polygon points="196,190 198,194 202,196 198,198 196,202 194,198 190,196 194,194" fill="#c59b27" opacity="0.85" />
          <polygon points="704,190 706,194 710,196 706,198 704,202 702,198 698,196 702,194" fill="#c59b27" opacity="0.85" />
          <polygon points="196,398 198,402 202,404 198,406 196,410 194,406 190,404 194,402" fill="#c59b27" opacity="0.85" />
          <polygon points="704,398 706,402 710,404 706,406 704,410 702,406 698,404 702,402" fill="#c59b27" opacity="0.85" />

          <!-- Top Highlight Tag -->
          <rect x="350" y="190" width="200" height="22" rx="11" fill="#fef3c7" stroke="#d97706" stroke-width="1" />
          <text x="450" y="205" text-anchor="middle" font-family="'Segoe UI', Roboto, sans-serif" font-size="10.5" font-weight="800" fill="#92400e" letter-spacing="3">✦ FESTIVE WISHES ✦</text>

          <!-- Dynamic Company Name -->
          <text x="450" y="248" text-anchor="middle" font-family="'Segoe UI', Roboto, -apple-system, sans-serif" font-size="${companyFontSize}" font-weight="900" fill="#78350f" letter-spacing="2.5">${sanitizedCompany.toUpperCase()}</text>

          <!-- Wishing Subtitle -->
          <text x="450" y="280" text-anchor="middle" font-family="'Cinzel', Georgia, 'Segoe UI', serif" font-size="11" font-weight="700" fill="#b45309" letter-spacing="3.5">WISHES YOU &amp; YOUR FAMILY A BLESSED</text>

          <!-- Luxury Gold Highlight Banner -->
          <rect x="190" y="298" width="520" height="46" rx="8" fill="#fef3c7" stroke="#b45309" stroke-width="1.6" />
          <line x1="190" y1="302" x2="710" y2="302" stroke="#f59e0b" stroke-width="1" stroke-dasharray="4,2" />
          <line x1="190" y1="340" x2="710" y2="340" stroke="#f59e0b" stroke-width="1" stroke-dasharray="4,2" />
          
          <polygon points="222,315 224,319 228,321 224,323 222,327 220,323 216,321 220,319" fill="#d97706" />
          <polygon points="678,315 680,319 684,321 680,323 678,327 676,323 672,321 676,319" fill="#d97706" />

          <text x="450" y="328" text-anchor="middle" font-family="'Segoe UI', Roboto, sans-serif" font-size="21" font-weight="800" fill="#92400e" letter-spacing="2.5">शुभ दीपावली &nbsp;•&nbsp; HAPPY DIWALI</text>

          <!-- Bottom Blessing -->
          <text x="450" y="372" text-anchor="middle" font-family="'Segoe UI', Roboto, sans-serif" font-size="9.5" font-weight="700" fill="#92400e" letter-spacing="3">✦ PEACE • PROSPERITY • HAPPINESS • SUCCESS ✦</text>

          <!-- Bottom Flourish Line -->
          <line x1="330" y1="392" x2="570" y2="392" stroke="#c59b27" stroke-width="1" stroke-dasharray="3,3" />
          <polygon points="450,388 453,392 450,396 447,392" fill="#c59b27" />
        </g>
      </svg>
    </div>
  `;
};

/**
 * Layout 3: Toran SVG across top header with hanging brass lamps
 */
const getToranBannerSVG = () => `
  <div class="diwali-toran-container">
    <svg class="diwali-toran-svg" viewBox="0 0 1000 64" fill="none" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
      <!-- Golden Main Rope -->
      <line x1="0" y1="10" x2="1000" y2="10" stroke="#c59b27" stroke-width="2.5" />
      <line x1="0" y1="13" x2="1000" y2="13" stroke="#e5c158" stroke-width="1" stroke-dasharray="4,3" />

      <!-- Marigold Festive Scallops -->
      <path d="M 0 10 Q 50 28 100 10 Q 150 28 200 10 Q 250 28 300 10 Q 350 28 400 10 Q 450 28 500 10 Q 550 28 600 10 Q 650 28 700 10 Q 750 28 800 10 Q 850 28 900 10 Q 950 28 1000 10" stroke="#f59e0b" stroke-width="3" fill="none" />
      <path d="M 0 10 Q 50 36 100 10 Q 150 36 200 10 Q 250 36 300 10 Q 350 36 400 10 Q 450 36 500 10 Q 550 36 600 10 Q 650 36 700 10 Q 750 36 800 10 Q 850 36 900 10 Q 950 36 1000 10" stroke="#d97706" stroke-width="1.5" stroke-dasharray="2,3" fill="none" />

      <!-- Hanging Golden Mango Leaf Accents (Aam Patta) -->
      <path d="M 50 18 Q 44 32 50 42 Q 56 32 50 18 Z" fill="#15803d" stroke="#166534" stroke-width="0.75" />
      <path d="M 150 18 Q 144 32 150 42 Q 156 32 150 18 Z" fill="#15803d" stroke="#166534" stroke-width="0.75" />
      <path d="M 250 18 Q 244 32 250 42 Q 256 32 250 18 Z" fill="#15803d" stroke="#166534" stroke-width="0.75" />
      <path d="M 350 18 Q 344 32 350 42 Q 356 32 350 18 Z" fill="#15803d" stroke="#166534" stroke-width="0.75" />
      <path d="M 450 18 Q 444 32 450 42 Q 456 32 450 18 Z" fill="#15803d" stroke="#166534" stroke-width="0.75" />
      <path d="M 550 18 Q 544 32 550 42 Q 556 32 550 18 Z" fill="#15803d" stroke="#166534" stroke-width="0.75" />
      <path d="M 650 18 Q 644 32 650 42 Q 656 32 650 18 Z" fill="#15803d" stroke="#166534" stroke-width="0.75" />
      <path d="M 750 18 Q 744 32 750 42 Q 756 32 750 18 Z" fill="#15803d" stroke="#166534" stroke-width="0.75" />
      <path d="M 850 18 Q 844 32 850 42 Q 856 32 850 18 Z" fill="#15803d" stroke="#166534" stroke-width="0.75" />
      <path d="M 950 18 Q 944 32 950 42 Q 956 32 950 18 Z" fill="#15803d" stroke="#166534" stroke-width="0.75" />

      <!-- Left Hanging Brass Diya Lamp -->
      <line x1="30" y1="10" x2="30" y2="44" stroke="#c59b27" stroke-width="1.2" stroke-dasharray="2,2" />
      <g transform="translate(20, 42) scale(0.35)">
        <path d="M6 34 C6 50, 50 50, 50 34 Z" fill="#b45309" stroke="#78350f" stroke-width="1"/>
        <ellipse cx="28" cy="33" rx="18" ry="3.5" fill="#f59e0b"/>
        <circle cx="28" cy="18" r="10" fill="#fef08a" opacity="0.45"/>
        <path d="M28 2 C22 12, 24 20, 28 24 C32 20, 34 12, 28 2 Z" fill="#ea580c"/>
        <path d="M28 8 C25 15, 26 19, 28 22 C30 19, 31 15, 28 8 Z" fill="#fef08a"/>
      </g>

      <!-- Right Hanging Brass Diya Lamp -->
      <line x1="970" y1="10" x2="970" y2="44" stroke="#c59b27" stroke-width="1.2" stroke-dasharray="2,2" />
      <g transform="translate(960, 42) scale(0.35)">
        <path d="M6 34 C6 50, 50 50, 50 34 Z" fill="#b45309" stroke="#78350f" stroke-width="1"/>
        <ellipse cx="28" cy="33" rx="18" ry="3.5" fill="#f59e0b"/>
        <circle cx="28" cy="18" r="10" fill="#fef08a" opacity="0.45"/>
        <path d="M28 2 C22 12, 24 20, 28 24 C32 20, 34 12, 28 2 Z" fill="#ea580c"/>
        <path d="M28 8 C25 15, 26 19, 28 22 C30 19, 31 15, 28 8 Z" fill="#fef08a"/>
      </g>
    </svg>
    <div class="diwali-top-banner" style="top: 14px;">
      <span class="diwali-sparkle">✦</span>
      <span class="diwali-badge-hindi">शुभ दीपावली</span>
      <span class="diwali-sparkle">•</span>
      <span class="diwali-badge-eng">HAPPY DIWALI</span>
      <span class="diwali-sparkle">✦</span>
    </div>
  </div>
`;

/**
 * Layout 4: Rangoli 8-point geometric corner star medallion
 */
const getRangoliCornerSVG = () => `
  <svg width="58" height="58" viewBox="0 0 58 58" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Corner Border Guide -->
    <path d="M2 56 L2 2 L56 2" stroke="#c59b27" stroke-width="2" stroke-linecap="round"/>
    <path d="M6 48 L6 6 L48 6" stroke="#e5c158" stroke-width="0.8" stroke-dasharray="2,2"/>
    <!-- Sacred Rangoli Geometric Star Motif -->
    <g transform="translate(20, 20)">
      <circle cx="10" cy="10" r="14" stroke="#c59b27" stroke-width="1" stroke-dasharray="2,2"/>
      <circle cx="10" cy="10" r="10" fill="none" stroke="#d97706" stroke-width="0.8"/>
      <!-- 8-Point Diamond Petals -->
      <polygon points="10,0 13,7 20,10 13,13 10,20 7,13 0,10 7,7" fill="#fef3c7" stroke="#b45309" stroke-width="0.8"/>
      <polygon points="10,3 12,8 17,10 12,12 10,17 8,12 3,10 8,8" fill="#f59e0b" opacity="0.8"/>
      <!-- Center Glowing Diya Flame -->
      <circle cx="10" cy="10" r="3" fill="#ea580c"/>
      <circle cx="10" cy="10" r="1.5" fill="#fef08a"/>
    </g>
    <!-- Little Corner Star -->
    <polygon points="46,12 47,14 49,15 47,16 46,18 45,16 43,15 45,14" fill="#c59b27"/>
  </svg>
`;

export const getDiwaliWatermarkHTML = (companyName = '', watermarkType = 'none') => {
  // Support flexible argument order if called as getDiwaliWatermarkHTML('diwali_frame')
  const validWatermarks = ['diwali_frame', 'diwali_wishes', 'diwali_diya', 'diwali_toran', 'diwali_rangoli', 'center', 'stylized', 'none'];
  if (validWatermarks.includes(companyName) && (!watermarkType || watermarkType === 'none')) {
    watermarkType = companyName;
    companyName = '';
  }

  if (!watermarkType || watermarkType === 'none') return '';

  if (watermarkType === 'center') {
    return `
      <div class="watermark-container">
        <div class="watermark">${companyName || 'INVOICE'}</div>
      </div>
    `;
  }

  if (watermarkType === 'stylized') {
    return `
      <div class="watermark-container">
        <div class="watermark stylized">${companyName || 'INVOICE'}</div>
      </div>
    `;
  }

  // 1. DIWALI FESTIVE FRAME
  if (watermarkType === 'diwali_frame') {
    return `
      <div class="diwali-frame-container">
        <div class="diwali-corner corner-tl">${getCornerDiyaSVG('tl')}</div>
        <div class="diwali-corner corner-tr">${getCornerDiyaSVG('tr')}</div>
        <div class="diwali-corner corner-bl">${getCornerDiyaSVG('bl')}</div>
        <div class="diwali-corner corner-br">${getCornerDiyaSVG('br')}</div>
        <div class="diwali-top-banner">
          <svg class="diwali-banner-diya" width="18" height="15" viewBox="0 0 24 20" fill="none">
            <path d="M3 14 C3 20, 21 20, 21 14 Z" fill="#b45309"/>
            <ellipse cx="12" cy="13.5" rx="8" ry="2" fill="#f59e0b"/>
            <path d="M12 2 C8 7, 9 11, 12 13 C15 11, 16 7, 12 2 Z" fill="#ea580c"/>
            <path d="M12 5 C10 8, 10.5 11, 12 12.5 C13.5 11, 14 8, 12 5 Z" fill="#fef08a"/>
          </svg>
          <span class="diwali-crest-leaf">❧</span>
          <span class="diwali-badge-hindi">शुभ दीपावली</span>
          <span class="diwali-sparkle">✦</span>
          <span class="diwali-badge-eng">HAPPY DIWALI</span>
          <span class="diwali-crest-leaf" style="transform: scaleX(-1);">❧</span>
          <svg class="diwali-banner-diya" width="18" height="15" viewBox="0 0 24 20" fill="none">
            <path d="M3 14 C3 20, 21 20, 21 14 Z" fill="#b45309"/>
            <ellipse cx="12" cy="13.5" rx="8" ry="2" fill="#f59e0b"/>
            <path d="M12 2 C8 7, 9 11, 12 13 C15 11, 16 7, 12 2 Z" fill="#ea580c"/>
            <path d="M12 5 C10 8, 10.5 11, 12 12.5 C13.5 11, 14 8, 12 5 Z" fill="#fef08a"/>
          </svg>
        </div>
        <div class="diwali-bottom-banner">
          <span class="diwali-sparkle">✦</span>
          <span class="diwali-flourish-diya">𑁍</span>
          <span class="diwali-sparkle">✦</span>
        </div>
      </div>
    `;
  }

  // 2. DIWALI WISHES WATERMARK - Commented out due to reported rendering bug
  // if (watermarkType === 'diwali_wishes' || watermarkType === 'diwali_diya') {
  //   return getCompanyDiwaliWatermarkHTML(companyName);
  // }

  // 3. DIWALI ROYAL TORAN
  if (watermarkType === 'diwali_toran') {
    return `
      ${getToranBannerSVG()}
      <div class="diwali-toran-border"></div>
    `;
  }

  // 4. DIWALI SHUBH LABH RANGOLI
  if (watermarkType === 'diwali_rangoli') {
    const rangoliCorner = getRangoliCornerSVG();
    return `
      <div class="diwali-rangoli-container">
        <div class="diwali-rangoli-corner rangoli-corner-tl">${rangoliCorner}</div>
        <div class="diwali-rangoli-corner rangoli-corner-tr">${rangoliCorner}</div>
        <div class="diwali-rangoli-corner rangoli-corner-bl">${rangoliCorner}</div>
        <div class="diwali-rangoli-corner rangoli-corner-br">${rangoliCorner}</div>
        
        <!-- Auspicious Shubh Labh Side Badges -->
        <div class="diwali-side-badge badge-shubh">
          <span class="side-badge-text">शुभ</span>
        </div>
        <div class="diwali-side-badge badge-labh">
          <span class="side-badge-text">लाभ</span>
        </div>

        <div class="diwali-top-banner">
          <span class="diwali-sparkle">✦</span>
          <span class="diwali-badge-hindi">शुभ दीपावली • HAPPY DIWALI</span>
          <span class="diwali-sparkle">✦</span>
        </div>
      </div>
    `;
  }

  return '';
};

export default {
  getDiwaliWatermarkCSS,
  getDiwaliWatermarkHTML
};
